
import { __ } from 'bits/intl'
import { Component, Prop, Vue } from 'bits/vue'
import OzBaseDialogBox, {
  OzBaseDialogBoxButtonData,
  OzContainedButtonColorScheme,
} from 'library/v4/components/OzBaseDialogBox.vue'
import OzInput from 'library/v4/components/OzInput.vue'
import type { StyleValue } from 'vue/types/jsx'

@Component({ components: { OzBaseDialogBox, OzInput } })
export default class OzInputDialogBox extends Vue {
  $refs!: {
    dialogInput: OzInput
  }

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Icon.
   */
  @Prop({ default: null }) iconSrc!: string | null

  /**
   * Icon alt text.
   */
  @Prop({ default: null }) iconAlt!: string | null

  /**
   * Title.
   */
  @Prop({ required: true }) title!: string

  /**
   * Subtitle.
   */
  @Prop({ required: false }) subtitle!: string

  /**
   * Input value.
   */
  @Prop({ default: '' }) value!: string

  /**
   * Input placeholder.
   */
  @Prop({ default: '' }) placeholder!: string

  /**
   * Input type.
   */
  @Prop({ default: 'text' }) type!: string

  /**
   * Input aria label.
   */
  @Prop({ default: '' }) ariaLabel!: string

  /**
   * Maximum input length
   */
  @Prop({ default: null }) maxLength!: number | null

  /**
   * Submit button text
   */
  @Prop() submitButtonText!: string

  /**
   * Cancel button text
   */
  @Prop() cancelButtonText!: string

  /**
   * Should submit button be disabled
   */
  @Prop({ default: false, required: false }) submitButtonDisabled!: boolean

  /**
   * Determines if each button should fill the width of the dialog
   */
  @Prop({ default: false }) forceFullWidthButtons!: boolean

  /**
   * Determines if the input is valid
   */
  @Prop({ default: true }) valid!: boolean

  /**
   * Validation message to display
   */
  @Prop({ default: '' }) validationMessage!: string

  /**
   * Style applied to content container div
   */
  @Prop({ default: null }) contentContainerStyle!: StyleValue

  get buttons(): OzBaseDialogBoxButtonData[] {
    const buttonList = [
      /**
       * Emits events passed in via the `buttons` prop.
       * @event cancel
       */
      {
        text: this.cancelButtonText || __('Cancel'),
        emit: 'cancel',
        colorScheme: OzContainedButtonColorScheme.Secondary,
        testId: 'cancelButton',
      },
      /**
       * Emits events passed in via the `buttons` prop.
       * @event submit
       */
      {
        text: this.submitButtonText || __('Submit'),
        emit: 'submit',
        colorScheme: OzContainedButtonColorScheme.Primary,
        disabled: this.submitButtonDisabled,
        testId: 'submitButton',
      },
    ]

    return this.forceFullWidthButtons ? buttonList.reverse() : buttonList
  }

  get localValue(): string {
    return this.value
  }

  set localValue(value) {
    /**
     * Update entered value.
     */
    this.$emit('update', value)
  }

  focus(): void {
    this.$nextTick(() => this.$refs?.dialogInput?.focusInput())
  }

  submit(): void {
    /**
     * Submit.
     */
    this.$emit('submit', this.localValue)
  }

  cancel(): void {
    /**
     * Cancel.
     */
    this.$emit('cancel')
  }
}
